import type { NextPage } from 'next'
import React, { useEffect, useState } from 'react'

import { createGetStaticProps } from '@hozana/api/server/static'
import { useIsHydrated } from '@hozana/hooks/useIsHydrated'
import { useQuery } from '@hozana/hooks/useQuery'
import { useRouter } from '@hozana/router'
import { getFirstQueryValue, getRedirectConnectDetails } from '@hozana/router/functions'

import { Block } from 'elements/layout/Block'
import { Div } from 'elements/layout/Div'
import { GradientDiv } from 'elements/ui/GradientDiv'
import { Loading } from 'elements/ui/Loading'

import { AUTH_SOURCE_EQUIVALENTS, QUERY_AUTH_SOURCE } from 'general/constants'
import { getConnectDetails, redirect, updateConnectDetails } from 'general/functions'
import { LanguageItem } from 'general/structure/Header/LanguageItem'
import { Page } from 'general/structure/Page'
import { NAMESPACE } from 'i18n/constants'
import { PAGE } from 'routes/constants'

import { ConnectForm } from 'modules/auth/components/ConnectForm'
import { CONNECT_FORM_WIDTH } from 'modules/auth/components/ConnectForm/constants'
import { loadToken } from 'modules/auth/functions'
import { useUser } from 'modules/user/hooks/useUser'

export const getStaticProps = createGetStaticProps(PAGE.CONNECT, 'home:home.connect.title', undefined, {
  namespaces: [NAMESPACE.HOME, NAMESPACE.AUTH],
})

export const ConnectPage: NextPage = () => {
  const isHydrated = useIsHydrated()

  const { query, locale } = useRouter()
  const {
    authSource: queryAuthSource,
    dwosu,
    email: queryEmail,
    redirectTo,
    redirectToExternal,
    title,
  } = getFirstQueryValue(query as { authSource?: QUERY_AUTH_SOURCE; [s: string]: string })

  const [email, setEmail] = useState(queryEmail)
  const [authSource, setAuthSource] = useState(AUTH_SOURCE_EQUIVALENTS[queryAuthSource])
  const [hasMaybeExpiredToken, setHasMaybeExpiredToken] = useState(!!loadToken())

  // Do not use `useUser({ redirectIf: 'loggedIn' })` to prevent redirections conflits
  // with redirections in auth/sagas
  const me = useUser()
  useEffect(() => {
    if (hasMaybeExpiredToken && !me.loading) {
      // Wait for me.isLogged in the token is wrong or expired
      if (me.isLogged) {
        redirect({
          ...(redirectTo ? getRedirectConnectDetails(redirectTo, locale) : getConnectDetails()),
          reloadUrl: redirectToExternal,
          reload: !!redirectToExternal,
        })
      }
      setHasMaybeExpiredToken(false)
    }
  }, [locale, me.isLogged, me.loading, redirectTo, redirectToExternal, hasMaybeExpiredToken])

  useQuery(
    () => {
      updateConnectDetails(getRedirectConnectDetails(redirectTo, locale))
    },
    [locale, redirectTo],
    'redirectTo',
  )
  useQuery(() => updateConnectDetails({ displayWizardOnSignup: !!dwosu }), [dwosu], 'dwosu')
  useQuery(
    () => {
      // Set the connect form email field to the email query value
      // Passing it as a prop does not work because the form is initialized on static generation, without any query
      if (queryEmail) {
        setEmail(queryEmail)
      }
    },
    [queryEmail],
    'email',
  )

  useQuery(() => setAuthSource(AUTH_SOURCE_EQUIVALENTS[queryAuthSource]), [queryAuthSource], 'authSource')

  return (
    <Page>
      <GradientDiv
        minH="100vh"
        firstColor="lightPurple"
        middleColor="lightBlue"
        lastColor="whiteOrange"
        p={{ xs: '0', sm: '0 10px 100px' }}
      >
        <Div align="left" p={{ xs: '5px', sm: '10px', md: '1em' }}>
          <LanguageItem />
        </Div>
        {isHydrated && (hasMaybeExpiredToken || me.loading || me.isLogged) ? (
          <Loading />
        ) : (
          <Block
            maxW={{ xs: '100%', sm: CONNECT_FORM_WIDTH }}
            m={{ xs: '0 auto', sm: '20px auto' }}
            minH={{ xs: '100vh', sm: 'unset' }}
          >
            <ConnectForm
              authSource={authSource}
              initialTitle={title}
              connectDetails={{ ...getConnectDetails(), reloadUrl: redirectToExternal, reload: !!redirectToExternal }}
              requestedEmail={email}
            />
          </Block>
        )}
      </GradientDiv>
    </Page>
  )
}
